import WidgetLoadingAnimation from 'components/Widgets/WidgetLoadingAnimation';
import WidgetTimeUpdate from 'components/Widgets/WidgetTimeUpdate';
import TimePeriodCard from 'features/Weather/components/TimePeriodCard/TimePeriodCard';
import useLocation from 'hooks/useLocation';
import { useMemo, useState } from 'react';
import {
  useGetDetailedFireDangerQuery,
  useGetExtendedFireDangerQuery,
} from 'services/weather/fireDanger/fireDanger.api';
import { joinClasses } from 'utils/componentStyles/style.utils';
import { formatDateTime } from 'utils/dates/date.utils';
import '../../assets/specialReportWidgets.scss';
import SpecialReportWidgetSelect from '../../components/SpecialReportWidgetSelect/SpecialReportWidgetSelect';
import SpecialReportWidgetState from '../../components/SpecialReportWidgetState/SpecialReportWidgetState';
import '../../features/FireDanger/assets/fireDangerCard.scss';
import FireDangerIcon from '../../features/FireDanger/components/FireDangerIcon/FireDangerIcon';
import {
  fireDangerCardClassNames,
  fireDangerDetails,
} from '../../features/FireDanger/config/fireDanger.config';
import {
  getFireDangerLocationNames,
  getFireDangerLocations,
  getFireDangerReportDate,
  getFireDangerTimeOfDay,
} from '../../features/FireDanger/utils/fireDanger.utils';
import useSpecialReportWidgetRefresh from '../../hooks/useSpecialReportWidgetRefresh';

const forecastOptions = [
  { value: 'extended', label: 'Extended forecast' },
  { value: 'detailed', label: 'Detailed forecast' },
];

const layoutConfig = {
  '1x1': { controlsClassName: 'special-report-widget-controls-paired' },
  '1x2': {},
  '2x1': { contentClassName: 'special-report-widget-content-wide' },
};

/** Loads the chosen fire forecast and shows one location and report at a time. */
const FireDangerWidget = ({ layout, dimensions, widgetRefresh }) => {
  const { locations } = useLocation();
  const location = locations.effectiveLocation;
  const isLocationLoading = !location || location.isLoading;
  const [forecastMode, setForecastMode] = useState('extended');
  const isDetailed = forecastMode === 'detailed';
  // The detailed forecast always loads: it is the only source of place names, which the extended
  // endpoint leaves out (a skipped RTK query never reads the cache, so it cannot lend them).
  const detailedQuery = useGetDetailedFireDangerQuery(location, {
    skip: isLocationLoading,
  });
  const extendedQuery = useGetExtendedFireDangerQuery(location, {
    skip: isLocationLoading || isDetailed,
  });
  const { data, isLoading, isFetching, isError, refetch } = isDetailed
    ? detailedQuery
    : extendedQuery;
  const route = isDetailed ? 'getDetailedFireDanger' : 'getExtendedFireDanger';

  useSpecialReportWidgetRefresh({
    route,
    location,
    widgetRefresh,
    refetch,
    skip: isLocationLoading,
  });

  const [selectedLocationId, setSelectedLocationId] = useState(null);
  const [selectedReportId, setSelectedReportId] = useState(null);
  // Reuse names from any detailed reports already loaded; extended reports omit place names.
  const forecastLocations = useMemo(
    () => getFireDangerLocations(data, getFireDangerLocationNames(detailedQuery.data)),
    [data, detailedQuery.data],
  );
  // Fall back to the first available choice if refreshed data no longer contains the selection.
  const activeLocation =
    forecastLocations.find((location) => location.id === selectedLocationId) ??
    forecastLocations[0];
  const reports = (activeLocation?.days ?? []).flatMap((day) => day.reports);
  const reportOptions = reports.map((report, index) => {
    // Use the same UTC-aware date as the page for both the selector and the card.
    const reportDate = getFireDangerReportDate(report);
    return {
      // The index keeps separate reports selectable even when their timestamps match.
      value: `${reportDate?.toISOString() ?? 'date-unavailable'}-${index}`,
      label: isDetailed
        ? `${getFireDangerTimeOfDay(report)} · ${formatDateTime(reportDate, 'short', 'Date unavailable')}`
        : formatDateTime(reportDate, 'short', 'Date unavailable', true),
      report,
    };
  });
  const activeReport =
    reportOptions.find((option) => option.value === selectedReportId) ?? reportOptions[0];
  const report = activeReport?.report;
  const reportDate = getFireDangerReportDate(report);
  const config = layoutConfig[layout] ?? layoutConfig['1x1'];

  // Keep the selected report visible while a background refresh is running.
  if (isLocationLoading || ((isLoading || isFetching) && !forecastLocations.length)) {
    return <WidgetLoadingAnimation widgetName="fireDanger" dimensions={dimensions} />;
  }

  return (
    <>
      <WidgetTimeUpdate route={route} realtime compact />
      <section
        aria-label="Fire danger widget"
        aria-busy={isFetching || undefined}
        className="special-report-widget d-flex flex-column h-100 min-h-0 gap-2 p-2"
        data-layout={layout}
      >
        <div className={joinClasses('special-report-widget-content', config.contentClassName)}>
          <div className={joinClasses('special-report-widget-controls', config.controlsClassName)}>
            {/* Keep forecast selection available even when one forecast has no reports. */}
            <SpecialReportWidgetSelect
              label="Forecast"
              options={forecastOptions}
              selectedValue={forecastMode}
              onSelect={(mode) => {
                setSelectedReportId(null);
                setForecastMode(mode);
              }}
            />
            {activeLocation && (
              <>
                <SpecialReportWidgetSelect
                  label="Location"
                  options={forecastLocations.map((location) => ({
                    value: location.id,
                    label: location.name,
                  }))}
                  selectedValue={activeLocation.id}
                  onSelect={(locationId) => {
                    setSelectedLocationId(locationId);
                    setSelectedReportId(null);
                  }}
                />
                <SpecialReportWidgetSelect
                  label="Forecast period"
                  options={reportOptions}
                  selectedValue={activeReport?.value}
                  onSelect={setSelectedReportId}
                />
              </>
            )}
          </div>
          <div className="special-report-widget-scroll">
            <SpecialReportWidgetState
              title="Fire Danger"
              isFetching={isFetching}
              isError={isError}
              hasData={Boolean(report)}
              onRetry={refetch}
              emptyMessage="No fire danger reports are available for this location."
            >
              {report && (
                <TimePeriodCard
                  data={report}
                  containerClassName={fireDangerCardClassNames[report.danger_level]}
                  title={
                    isDetailed
                      ? getFireDangerTimeOfDay(report)
                      : formatDateTime(reportDate, 'short', 'Date unavailable', true)
                  }
                  subtitle={
                    isDetailed && reportDate ? (
                      <time dateTime={reportDate.toISOString()}>{formatDateTime(reportDate)}</time>
                    ) : !isDetailed ? (
                      activeLocation.name
                    ) : (
                      'Date unavailable'
                    )
                  }
                  subtitleIcon={<FireDangerIcon dangerLevel={report.danger_level} size={32} />}
                  details={fireDangerDetails}
                  headingLevel="h3"
                />
              )}
            </SpecialReportWidgetState>
          </div>
        </div>
      </section>
    </>
  );
};

export default FireDangerWidget;
